import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { generatePath } from 'react-router-dom'

import api from '@src/api/api'

import type { Role, RoleCreatePayload, RoleUpdatePayload, PermissionCatalogEntry } from '@models/Role'

import { useCreate, useDelete, useGetList, useGetOne, usePatch } from '../request'
import { AccountKeys, OrgStructureKeys, RolesKeys } from '../Keys'

const PATHS = {
  list: '/api/roles/',
  detail: '/api/roles/:id/',
  assign: '/api/roles/:id/assign/',
  unassign: '/api/roles/:id/assign/:teamUserId/',
  catalog: '/api/roles/permissions-catalog/',
  memberRoles: '/api/roles/members/:teamUserId/',
  memberRolesBulk: '/api/roles/members/bulk/',
}

// react-router-dom v6's `generatePath` strips trailing slashes from
// the pattern, which Django rejects on data-carrying methods (POST /
// PUT / PATCH raise RuntimeError when APPEND_SLASH would redirect).
// Always go through this helper so URLs land with the trailing slash
// Django expects.
const makePath = (pattern: string, params: Parameters<typeof generatePath>[1]): string =>
  `${generatePath(pattern, params)}/`

const invalidateAll = (cache: ReturnType<typeof useQueryClient>) => {
  cache.invalidateQueries([RolesKeys.Roles])
  // /me carries the user's permissions; invalidate it so the current
  // user's permission set refreshes after a role edit affects them.
  cache.invalidateQueries([AccountKeys.Me])
  // The Members table + org-structure tree render role chips; refresh so role
  // rename / permission / assignment changes show immediately. Keyed under
  // OrgStructureKeys.OrgStructure — the ['TeamUsers'] key that used to be
  // invalidated at each call site never existed, so it was a silent no-op.
  cache.invalidateQueries([OrgStructureKeys.OrgStructure])
}

export const useGetRoles = () => {
  return useGetList<Role>([RolesKeys.Roles], PATHS.list)
}

export const useGetRole = (id: number | null | undefined) => {
  return useGetOne<Role>([RolesKeys.Role, id], makePath(PATHS.detail, { id: String(id ?? '') }), {
    query: { enabled: id !== null && id !== undefined },
  })
}

export const useGetPermissionsCatalog = () => {
  return useGetList<PermissionCatalogEntry>(
    [RolesKeys.PermissionsCatalog],
    PATHS.catalog,
    // Catalog is static for the lifetime of the app — long stale time.
    { query: { staleTime: 24 * 60 * 60 * 1000, cacheTime: 24 * 60 * 60 * 1000 } },
  )
}

export const useCreateRole = () => {
  const cache = useQueryClient()
  return useCreate<RoleCreatePayload, Role>(PATHS.list, {
    mutation: { onSuccess: () => invalidateAll(cache) },
  })
}

export const useUpdateRole = (id: number) => {
  const cache = useQueryClient()
  return usePatch<RoleUpdatePayload>(makePath(PATHS.detail, { id: String(id) }), {
    mutation: {
      onSuccess: () => {
        invalidateAll(cache)
        // Refresh the open role detail (useGetRole caches under
        // [RolesKeys.Role, id]); invalidateAll only hits the list + /me.
        cache.invalidateQueries([RolesKeys.Role, id])
      },
    },
  })
}

export const useDeleteRole = (id: number) => {
  const cache = useQueryClient()
  return useDelete(makePath(PATHS.detail, { id: String(id) }), {
    mutation: {
      onSuccess: () => {
        invalidateAll(cache)
        // The role is gone — drop its detail cache so a stale editor/detail
        // view can't keep rendering it.
        cache.removeQueries([RolesKeys.Role, id])
      },
    },
  })
}

export const useAssignRole = (roleId: number) => {
  const cache = useQueryClient()
  return useCreate<{ team_user_id: number }>(makePath(PATHS.assign, { id: String(roleId) }), {
    mutation: {
      onSuccess: () => invalidateAll(cache),
    },
  })
}

/**
 * Dynamic-target version of useAssignRole: takes both ids at mutation
 * time, so a picker with N candidate roles needs ONE hook instance
 * instead of N. Mirrors the invalidation set of useAssignRole exactly
 * (Roles + Me + OrgStructure via invalidateAll) — call sites switching
 * off the old imperative path don't regress the Members page cache.
 */
export const useAssignRoleDynamic = () => {
  const cache = useQueryClient()
  return useMutation<unknown, AxiosError, { roleId: number; teamUserId: number }>(
    async ({ roleId, teamUserId }) => {
      const path = makePath(PATHS.assign, { id: String(roleId) })
      const { data } = await api.post(path, { team_user_id: teamUserId })
      return data
    },
    {
      onSuccess: () => invalidateAll(cache),
    },
  )
}

/**
 * Remove a single role assignment from a TeamUser. The backend
 * rejects the call if it would leave the user with zero roles
 * (must always have at least one).
 */
export const useUnassignRole = (roleId: number, teamUserId: number) => {
  const cache = useQueryClient()
  return useDelete(makePath(PATHS.unassign, { id: String(roleId), teamUserId: String(teamUserId) }), {
    mutation: {
      onSuccess: () => invalidateAll(cache),
    },
  })
}

/**
 * Reconcile a single TeamUser's role set to an exact list. Backend
 * adds/removes assignments in one transaction. Used by the inline
 * multi-pick picker on the Members page.
 */
export const useReplaceMemberRoles = (teamUserId: number) => {
  const cache = useQueryClient()
  return useMutation<unknown, AxiosError, { role_ids: number[] }>(
    async (body) => {
      const path = makePath(PATHS.memberRoles, { teamUserId: String(teamUserId) })
      const { data } = await api.put(path, body)
      return data
    },
    {
      onSuccess: () => invalidateAll(cache),
    },
  )
}

export type BulkRolesPayload =
  | { team_user_ids: number[]; add: number[] }
  | { team_user_ids: number[]; remove: number[] }
  | { team_user_ids: number[]; replace: number[] }

export type BulkRolesResult = {
  processed: number
  results: Array<{
    team_user_id: number
    added?: number[]
    removed?: number[]
    error?: unknown
  }>
}

/**
 * Bulk add/remove/replace roles across N TeamUsers in one transaction.
 * Per-user errors (e.g. a remove that would leave a user with zero
 * roles) are reported in the results array, not raised as a top-level
 * failure.
 */
export const useBulkMemberRoles = () => {
  const cache = useQueryClient()
  return useMutation<BulkRolesResult, AxiosError, BulkRolesPayload>(
    async (body) => {
      const { data } = await api.post(PATHS.memberRolesBulk, body)
      return data
    },
    {
      onSuccess: () => invalidateAll(cache),
    },
  )
}

export { RolesKeys as Keys }
